import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  Clock,
  AlertTriangle,
  User,
  Calendar,
  RotateCcw,
  Hourglass,
  Flag,
} from "lucide-react";
import type { ChamadoKanban } from "../../../types/api";
import {
  ehNovaFuncionalidade,
  formatTempoRestante,
  getStatusClasses,
  isSLAPausado,
  isNaFilaSLA,
  normalizeStatus,
} from "../../../utils/chamadoUtils";
import { useKanbanData } from "../../../hooks/useKanbanData";
import { CountdownTimer } from "../../../components/ui/CountdownTimer";
import { TooltipWrapper } from "../../../components/ui/Tooltip";
import { PrioridadeEditor } from "./PrioridadeEditor";
import { useUpdatePrioridade } from "../hooks/useChamados";
import { useAuthStore } from "../../../stores/authStore";
import {
  AnexosRapidos,
  EtiquetaAcoesCliente,
  EtiquetaStatusEquipe,
  RespostaRapida,
} from "../../equipe/AcoesRapidasCartao";
import { useCapacidades } from "../gestao/api";
import { STATUS_DA_COLUNA } from "../../equipe/movimento";
import { useMudarStatus } from "../../equipe/api";
import { mensagemDeErro } from "../gestao/api";

interface KanbanBoardProps {
  chamados: ChamadoKanban[];
  onChamadoClick?: (chamadoId: number) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /** Quando o servidor respondeu, em epoch. Zero enquanto nunca respondeu. */
  dataUpdatedAt?: number;
  selectedChamadoId?: number | null;
  isLoadingDetails?: boolean;
}

const StatusCard = ({
  coluna,
  arrastavel = false,
  title,
  chamados,
  color,
  onChamadoClick,
  onPrioridadeUpdate,
  isPrioridadeUpdating,
  isRefreshing,
  selectedChamadoId,
  isLoadingDetails,
  showResponseTime = false,
}: {
  /** A chave da coluna (Solicitados, Em andamento, Concluídos): é o alvo do arraste. */
  coluna: string;
  arrastavel?: boolean;
  title: string;
  chamados: ChamadoKanban[];
  color: string;
  onChamadoClick?: (chamadoId: number) => void;
  onPrioridadeUpdate?: (id: number, prioridade: number | null) => void;
  isPrioridadeUpdating?: boolean;
  isRefreshing?: boolean;
  selectedChamadoId?: number | null;
  isLoadingDetails?: boolean;
  showResponseTime?: boolean;
}) => {
  const getColorClasses = (color: string) => {
    switch (color) {
      case "blue":
        return {
          header: "bg-gradient-to-r from-blue-500 to-blue-400",
          bg: "bg-blue-50/50",
          border: "border-blue-200/50",
        };
      case "yellow":
        return {
          header: "bg-gradient-to-r from-yellow-500 to-yellow-400",
          bg: "bg-yellow-50/50",
          border: "border-yellow-200/50",
        };
      case "green":
        return {
          header: "bg-gradient-to-r from-green-500 to-green-400",
          bg: "bg-green-50/50",
          border: "border-green-200/50",
        };
      default:
        return {
          header: "bg-gradient-to-r from-gray-500 to-gray-400",
          bg: "bg-gray-50/50",
          border: "border-gray-200/50",
        };
    }
  };

  const colors = getColorClasses(color);
  const { setNodeRef, isOver } = useDroppable({
    id: coluna,
    disabled: !arrastavel,
  });

  return (
    // No celular, uma coluna por tela e o dedo desliza para a próxima, como no
    // Trello (06/10/2026); do tablet para cima, as colunas lado a lado.
    <div className="w-[85vw] max-w-sm shrink-0 snap-center md:w-auto md:max-w-none md:flex-1 md:min-w-80">
      {/* Header da coluna */}
      <div className={`${colors.header} rounded-t-xl p-4 shadow-lg`}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-lg">{title}</h3>
          <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
            {chamados.length}
          </span>
        </div>
      </div>

      {/* Conteúdo da coluna */}
      <div
        ref={setNodeRef}
        className={`${colors.bg} ${
          colors.border
        } border-l border-r border-b rounded-b-xl min-h-96 p-4 space-y-4 backdrop-blur-sm relative transition-shadow ${
          isRefreshing ? "opacity-70" : ""
        } ${isOver ? "ring-2 ring-blue-400 ring-inset" : ""}`}
      >
        {/* Overlay de loading */}
        {isRefreshing && (
          <div className="absolute inset-0 bg-white/20 backdrop-blur-sm rounded-b-xl flex items-center justify-center z-10">
            <div className="bg-white/90 rounded-lg px-3 py-2 flex items-center space-x-2 shadow-lg">
              <RotateCcw className="w-4 h-4 animate-spin text-blue-600" />
              <span className="text-sm font-medium text-gray-700">
                Atualizando...
              </span>
            </div>
          </div>
        )}
        {chamados.map((chamado) => (
          <Arrastavel key={chamado.id} chamado={chamado} ativo={arrastavel}>
            <ChamadoCard
              chamado={chamado}
              onClick={() => onChamadoClick?.(chamado.id)}
              onPrioridadeUpdate={onPrioridadeUpdate}
              isPrioridadeUpdating={isPrioridadeUpdating}
              isSelected={selectedChamadoId === chamado.id}
              isLoading={selectedChamadoId === chamado.id && isLoadingDetails}
              showResponseTime={showResponseTime}
            />
          </Arrastavel>
        ))}
        {chamados.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <p className="font-medium">Nenhum chamado</p>
            <p className="text-sm">neste status</p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * O cartão que a equipe arrasta para outra coluna (07/10/2026). Só com o mouse e depois de
 * andar alguns pixels: o clique continua abrindo o chamado, e no celular o dedo segue rolando
 * entre as colunas — lá, a mudança de status é pela etiqueta.
 */
const Arrastavel = ({
  chamado,
  ativo,
  children,
}: {
  chamado: ChamadoKanban;
  ativo: boolean;
  children: ReactNode;
}) => {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: chamado.id,
    data: { chamado },
    disabled: !ativo,
  });
  if (!ativo) return <>{children}</>;
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role="group"
      aria-roledescription="cartão arrastável"
      className={isDragging ? "opacity-30" : undefined}
    >
      {children}
    </div>
  );
};

const ChamadoCard = ({
  chamado,
  onClick,
  onPrioridadeUpdate,
  isPrioridadeUpdating,
  isSelected = false,
  isLoading = false,
  showResponseTime = false,
}: {
  chamado: ChamadoKanban;
  onClick?: () => void;
  onPrioridadeUpdate?: (id: number, prioridade: number | null) => void;
  isPrioridadeUpdating?: boolean;
  isSelected?: boolean;
  isLoading?: boolean;
  showResponseTime?: boolean;
}) => {
  const equipe = useAuthStore((state) => Boolean(state.user?.equipe));
  // Escrita desligada no ambiente: a etiqueta volta a ser só etiqueta, e o balão some.
  const { data: capacidades } = useCapacidades();
  const escreve = Boolean(capacidades?.escritasHabilitadas);

  // Obter severidade para determinar o badge
  const severidadeNum =
    chamado.slaInfo?.severidadeAtendimento?.numeroSeveridade ||
    chamado.severidade;

  // Definir estilo do badge de severidade
  const getSeveridadeBadge = (num: number) => {
    switch (num) {
      case 1:
        return {
          label: "CRÍTICA",
          class: "bg-red-100 text-red-800 border-red-300 shadow-red-100",
          dotClass: "bg-red-500",
        };
      case 2:
        return {
          label: "ALTA",
          class:
            "bg-orange-100 text-orange-800 border-orange-300 shadow-orange-100",
          dotClass: "bg-orange-500",
        };
      case 3:
        return {
          label: "MÉDIA",
          class:
            "bg-yellow-100 text-yellow-800 border-yellow-300 shadow-yellow-100",
          dotClass: "bg-yellow-500",
        };
      case 4:
        return {
          label: "BAIXA",
          class: "bg-blue-100 text-blue-800 border-blue-300 shadow-blue-100",
          dotClass: "bg-blue-500",
        };
      default:
        return {
          label: "NORMAL",
          class: "bg-gray-100 text-gray-800 border-gray-300 shadow-gray-100",
          dotClass: "bg-gray-500",
        };
    }
  };

  const severidadeBadge = getSeveridadeBadge(severidadeNum);
  const isNovasFuncionalidades = ehNovaFuncionalidade(chamado.categoria);
  const dataPrevisao = chamado.dataPrevisao;
  const shouldShowPrazoPrevisao =
    isNovasFuncionalidades && Boolean(dataPrevisao);
  const slaStatusLower = chamado.slaInfo?.statusSla?.toLowerCase() ?? "";
  const isNaFila = isNaFilaSLA(chamado.slaInfo?.statusSla);
  const isAtivoNaFila = chamado.slaInfo?.statusFila === "ATIVO";
  const isSlaPausadoStatus =
    !isNaFila &&
    (isSLAPausado(chamado.status.nome) || slaStatusLower.includes("pausado"));
  const tempoConclusaoLabel = showResponseTime
    ? "Tempo de Conclusão"
    : "Tempo restante";
  const tempoConclusaoTexto = (() => {
    const valor = chamado.slaInfo?.tempoRestanteConclusao;
    if (!valor) return "Não informado";
    const formatado = formatTempoRestante(valor);
    return formatado || valor;
  })();
  const renderTempoResposta = () => {
    if (!showResponseTime) return null;
    const valor = chamado.slaInfo?.tempoRestanteResposta;
    if (!valor) {
      return (
        <div className="flex items-center text-xs text-gray-600 bg-purple-50 p-2 rounded-lg border border-purple-200">
          <Hourglass className="w-3 h-3 mr-2 text-purple-600" />
          <span className="font-medium">Tempo de Resposta:</span>&nbsp;Não
          informado
        </div>
      );
    }

    // Verifica se deve usar countdown (horário comercial + severidade não crítica + SLA não pausado + não vencido)
    const shouldUseCountdown =
      chamado.slaInfo?.eraHorarioComercial &&
      severidadeNum !== 1 && // Exclui crítica (1)
      !isSlaPausadoStatus &&
      !chamado.slaInfo?.slaVencido;

    if (shouldUseCountdown) {
      return (
        <div className="flex items-center text-xs text-gray-600 bg-purple-50 p-2 rounded-lg border border-purple-200">
          <Hourglass className="w-3 h-3 mr-2 text-purple-600" />
          <span className="font-medium">Tempo de Resposta:</span>&nbsp;
          <CountdownTimer
            tempoRestante={valor}
            slaVencido={chamado.slaInfo?.slaVencido ?? false}
            statusSLA={chamado.slaInfo?.statusSla}
            statusChamado={chamado.status.nome}
            className="bg-transparent border-none text-gray-600 p-0"
          />
        </div>
      );
    }

    const formatado = formatTempoRestante(valor);
    return (
      <div className="flex items-center text-xs text-gray-600 bg-purple-50 p-2 rounded-lg border border-purple-200">
        <Hourglass className="w-3 h-3 mr-2 text-purple-600" />
        <span className="font-medium">Tempo de Resposta:</span>&nbsp;
        {formatado || valor}
      </div>
    );
  };

  // Função para renderizar tempo de conclusão (com countdown quando aplicável)
  const renderTempoConclusao = () => {
    const valor = chamado.slaInfo?.tempoRestanteConclusao;
    if (!valor) return null;

    // Verifica se deve usar countdown (horário comercial + severidade não crítica + SLA não pausado + não vencido)
    const shouldUseCountdown =
      chamado.slaInfo?.eraHorarioComercial &&
      severidadeNum !== 1 && // Exclui crítica (1)
      !isSlaPausadoStatus &&
      !chamado.slaInfo?.slaVencido;

    if (shouldUseCountdown) {
      return (
        <div className="flex items-center text-xs text-gray-600 bg-orange-50 p-2 rounded-lg border border-orange-200">
          <Hourglass className="w-3 h-3 mr-2 text-orange-600" />
          <span className="font-medium">{tempoConclusaoLabel}:</span>&nbsp;
          <CountdownTimer
            tempoRestante={valor}
            slaVencido={chamado.slaInfo?.slaVencido ?? false}
            statusSLA={chamado.slaInfo?.statusSla}
            statusChamado={chamado.status.nome}
            className="bg-transparent border-none text-gray-600 p-0"
          />
        </div>
      );
    }

    return (
      <div className="flex items-center text-xs text-gray-600 bg-orange-50 p-2 rounded-lg border border-orange-200">
        <Hourglass className="w-3 h-3 mr-2 text-orange-600" />
        <span className="font-medium">{tempoConclusaoLabel}:</span>&nbsp;
        {tempoConclusaoTexto}
      </div>
    );
  };

  return (
    <div
      className={`bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group hover:scale-[1.01] border relative ${
        isAtivoNaFila
          ? "border-blue-300 ring-1 ring-blue-200"
          : "border-gray-100 hover:border-gray-200"
      } ${
        isSelected && isLoading ? "ring-2 ring-blue-400 ring-opacity-50" : ""
      } ${isSelected && isLoading ? "opacity-75" : ""}`}
      onClick={onClick}
    >
      {/* Loading overlay quando selecionado */}
      {isSelected && isLoading && (
        <div className="absolute inset-0 bg-blue-50/80 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-lg px-3 py-2 flex items-center space-x-2 shadow-lg">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm font-medium text-gray-700">
              Carregando...
            </span>
          </div>
        </div>
      )}
      {/* Topo do card - ID/Data à esquerda, Badge de severidade à direita */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex flex-col">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            {isAtivoNaFila && (
              <TooltipWrapper tooltip="Ativo na fila SLA" placement="bottom">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              </TooltipWrapper>
            )}
            <span className="font-mono font-semibold text-gray-700">
              #{chamado.id}
            </span>
            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
            <div className="flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              <span>
                {new Date(chamado.dataCriacao).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                })}{" "}
                -{" "}
                {new Date(chamado.dataCriacao).toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        </div>

        {isNovasFuncionalidades ? (
          onPrioridadeUpdate ? (
            <PrioridadeEditor
              chamadoId={chamado.id}
              prioridade={chamado.prioridade}
              onSave={onPrioridadeUpdate}
              isPending={isPrioridadeUpdating}
              variant="badge"
            />
          ) : chamado.prioridade != null ? (
            <TooltipWrapper tooltip="Prioridade do chamado" placement="bottom">
              <div className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold rounded-lg border shadow-sm bg-gradient-to-r from-slate-100 to-slate-50 text-slate-800 border-slate-200">
                <Flag className="w-3 h-3 text-slate-600" />
                <span>{chamado.prioridade}</span>
              </div>
            </TooltipWrapper>
          ) : (
            <TooltipWrapper
              tooltip="Prioridade não definida"
              placement="bottom"
            >
              <div className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-lg border border-dashed border-amber-300 bg-amber-50 text-amber-600">
                <Flag className="w-3 h-3 text-amber-400" />
                <span>S/ prioridade</span>
              </div>
            </TooltipWrapper>
          )
        ) : (
          <div
            className={`inline-flex items-center px-2.5 py-1.5 text-xs font-semibold rounded-full border shadow-sm ${severidadeBadge.class}`}
          >
            <div
              className={`w-2 h-2 rounded-full mr-1.5 ${severidadeBadge.dotClass}`}
            ></div>
            {severidadeBadge.label}
          </div>
        )}
      </div>

      {/* Título do chamado */}
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">
          {chamado.assunto}
        </h4>
      </div>

      {/* Descrição do chamado */}
      {chamado.descricao && (
        <div className="mb-4">
          <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
            {chamado.descricao}
          </p>
        </div>
      )}

      {/* Responsável */}
      <div className="flex items-center text-xs text-gray-600 mb-3">
        <div className="w-5 h-5 bg-gradient-to-br from-blue-600 to-blue-500 rounded-full flex items-center justify-center mr-2">
          <User className="w-2.5 h-2.5 text-white" />
        </div>
        <span className="truncate font-medium">{chamado.responsavel}</span>
      </div>

      {/* Status e Categoria em linha. A etiqueta de status abre, ali mesmo, a mudança de status
          (equipe T4i) ou as ações do cliente; o balão registra uma interação rápida (05/10/2026). */}
      <div className="flex items-center gap-2 mb-4">
        {escreve && equipe ? (
          <EtiquetaStatusEquipe
            idChamado={chamado.id}
            idStatusAtual={chamado.status.id}
            nomeStatusAtual={chamado.status.nome}
            className={`px-2.5 py-1 text-xs rounded-lg border font-medium ${getStatusClasses(chamado.status.nome)}`}
          />
        ) : escreve ? (
          <EtiquetaAcoesCliente
            idChamado={chamado.id}
            nomeStatusAtual={chamado.status.nome}
            className={`px-2.5 py-1 text-xs rounded-lg border font-medium ${getStatusClasses(chamado.status.nome)}`}
          />
        ) : (
          <span
            className={`inline-flex px-2.5 py-1 text-xs rounded-lg border font-medium ${getStatusClasses(
              chamado.status.nome,
            )}`}
          >
            {chamado.status.nome}
          </span>
        )}

        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-slate-50 text-slate-700 rounded-lg border border-slate-200">
          {chamado.categoria}
        </span>

        {escreve && <RespostaRapida idChamado={chamado.id} equipe={equipe} />}
        {(chamado.qtdAnexos ?? 0) > 0 && (
          <AnexosRapidos
            idChamado={chamado.id}
            quantidade={chamado.qtdAnexos!}
          />
        )}
      </div>

      {/* SLA indicator */}
      {!isNovasFuncionalidades && chamado.slaInfo && (
        <div className="space-y-2">
          {/* Status do SLA */}
          <div
            className={`flex items-center text-xs p-2 rounded-lg border ${
              isNaFila
                ? "bg-slate-50 text-slate-600 border-slate-200"
                : isSlaPausadoStatus
                  ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                  : chamado.slaInfo.slaVencido
                    ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-green-50 text-green-700 border-green-200"
            }`}
          >
            {chamado.slaInfo.slaVencido ? (
              <AlertTriangle className="w-3 h-3 mr-2" />
            ) : (
              <Clock className="w-3 h-3 mr-2" />
            )}
            <span className="font-medium">
              SLA: {chamado.slaInfo.statusSla}
            </span>
          </div>

          {/* Na fila SLA - mostra tempo total do SLA (ainda não iniciou) */}
          {isNaFila && chamado.slaInfo.tempoConclusaoAplicado > 0 && (
            <div className="flex items-center text-xs text-gray-600 bg-indigo-50 p-2 rounded-lg border border-indigo-200">
              <Hourglass className="w-3 h-3 mr-2 text-slate-500" />
              <span className="font-medium">Tempo de SLA:</span>&nbsp;
              {chamado.slaInfo.tempoConclusaoAplicado}h (após ativação)
            </div>
          )}

          {/* Tempo restante caso SLA pausado - mostra o tempo que faltaria */}
          {isSlaPausadoStatus && (
            <div className="space-y-2">
              {renderTempoResposta()}
              {renderTempoConclusao()}
            </div>
          )}

          {/* Tempo restante para conclusão - só mostra se não estiver vencido, não pausado e não na fila */}
          {!chamado.slaInfo.slaVencido &&
            !isSlaPausadoStatus &&
            !isNaFila &&
            chamado.slaInfo.tempoRestanteConclusao && (
              <div className="space-y-2">
                {renderTempoResposta()}
                {renderTempoConclusao()}
              </div>
            )}

          {/* Data limite de conclusão - só mostra se não estiver vencido, não pausado, não na fila e houver data */}
          {!chamado.slaInfo.slaVencido &&
            !isSlaPausadoStatus &&
            !isNaFila &&
            chamado.slaInfo.dataLimiteConclusao && (
              <div className="flex items-center text-xs text-gray-600 bg-blue-50 p-2 rounded-lg border border-blue-200">
                <Calendar className="w-3 h-3 mr-2" />
                <span className="font-medium">
                  Prazo:{" "}
                  {new Date(
                    chamado.slaInfo.dataLimiteConclusao,
                  ).toLocaleDateString("pt-BR")}{" "}
                  às{" "}
                  {new Date(
                    chamado.slaInfo.dataLimiteConclusao,
                  ).toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            )}
        </div>
      )}

      {shouldShowPrazoPrevisao && dataPrevisao && (
        <div className="mt-3 flex items-center text-xs text-gray-600 bg-slate-50 p-2 rounded-lg border border-slate-200">
          <Calendar className="w-3 h-3 mr-2" />
          <span className="font-medium">
            Prazo: {new Date(dataPrevisao).toLocaleDateString("pt-BR")}
          </span>
        </div>
      )}
    </div>
  );
};

export const KanbanBoard = ({
  chamados,
  onChamadoClick,
  onRefresh,
  isRefreshing,
  dataUpdatedAt = 0,
  selectedChamadoId,
  isLoadingDetails = false,
}: KanbanBoardProps) => {
  // Arrastar entre colunas (07/10/2026): só a equipe, com a escrita ligada — a mesma regra
  // da etiqueta de status do cartão. Soltar grava na hora, sem confirmação (pedido de
  // 07/10/2026): o cartão já aparece na coluna nova enquanto a API responde e volta se ela recusar.
  const equipe = useAuthStore((state) => Boolean(state.user?.equipe));
  const { data: capacidades } = useCapacidades();
  const arrastavel = equipe && Boolean(capacidades?.escritasHabilitadas);
  const sensores = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
  );
  const mudarStatus = useMudarStatus();
  const [arrastando, setArrastando] = useState<ChamadoKanban | null>(null);
  const [movidos, setMovidos] = useState<
    Record<number, { id: number; nome: string }>
  >({});
  const [erroMover, setErroMover] = useState<string | null>(null);

  // O status novo vale na tela até a lista recarregada trazê-lo.
  const vistos = useMemo(
    () =>
      (chamados || []).map((c) =>
        movidos[c.id] ? { ...c, status: { ...c.status, ...movidos[c.id] } } : c,
      ),
    [chamados, movidos],
  );
  useEffect(() => {
    setMovidos((atual) => {
      const restantes = Object.fromEntries(
        Object.entries(atual).filter(
          ([id, destino]) =>
            !(chamados || []).some(
              (c) => c.id === Number(id) && c.status.id === destino.id,
            ),
        ),
      );
      return Object.keys(restantes).length === Object.keys(atual).length
        ? atual
        : restantes;
    });
  }, [chamados]);

  const { chamadosPorStatus, statusConfig, isEmpty } = useKanbanData(vistos);
  const { mutate: updatePrioridade, isPending: isPrioridadeUpdating } =
    useUpdatePrioridade();

  const [larguraArrastado, setLarguraArrastado] = useState<number | null>(null);
  const aoComecar = (e: DragStartEvent) => {
    setLarguraArrastado(e.active.rect.current.initial?.width ?? null);
    setArrastando((e.active.data.current?.chamado as ChamadoKanban) ?? null);
  };
  const aoSoltar = (e: DragEndEvent) => {
    setArrastando(null);
    const chamado = e.active.data.current?.chamado as ChamadoKanban | undefined;
    const coluna = e.over?.id as string | undefined;
    if (!chamado || !coluna || coluna === normalizeStatus(chamado.status.nome))
      return;
    const destino = STATUS_DA_COLUNA[coluna];
    if (!destino) return;
    setErroMover(null);
    setMovidos((atual) => ({ ...atual, [chamado.id]: destino }));
    mudarStatus.mutate(
      { idChamado: chamado.id, idStatus: destino.id },
      {
        onError: (erro) => {
          setMovidos((atual) => {
            const resto = { ...atual };
            delete resto[chamado.id];
            return resto;
          });
          setErroMover(
            `#${chamado.id} não foi para ${destino.nome}: ${mensagemDeErro(erro, "a mudança foi recusada.")}`,
          );
        },
      },
    );
  };

  // Celular: qual coluna está na tela, para marcar a aba. A largura de uma
  // coluna mais o espaço entre elas é o passo do deslize.
  const colunas = Object.entries(statusConfig);
  const trilho = useRef<HTMLDivElement>(null);
  const [colunaAtiva, setColunaAtiva] = useState(0);
  const passo = () => {
    const primeira = trilho.current?.firstElementChild as HTMLElement | null;
    return primeira ? primeira.offsetWidth + 12 : 1;
  };
  const aoRolar = () => {
    if (!trilho.current) return;
    setColunaAtiva(Math.round(trilho.current.scrollLeft / passo()));
  };
  const irParaColuna = (i: number) =>
    trilho.current?.scrollTo({ left: i * passo(), behavior: "smooth" });

  // Estado para controlar horário da última atualização
  // Horário real da resposta do servidor. Antes isto era um new Date() no
  // efeito, o que marcava quando o componente recebeu os dados: com cache do
  // React Query, um dado de minutos atrás aparecia como "agora".
  const atualizadoEm = dataUpdatedAt ? new Date(dataUpdatedAt) : null;
  const minutosDesdeAtualizacao = atualizadoEm
    ? Math.floor((Date.now() - atualizadoEm.getTime()) / 60000)
    : null;
  const desatualizado = (minutosDesdeAtualizacao ?? 0) >= 5;

  // Função para lidar com o refresh
  const handleRefresh = () => {
    if (onRefresh && !isRefreshing) onRefresh();
  };

  if (isEmpty) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Nenhum chamado encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header do Kanban */}
      <div className="flex items-center justify-between">
        <h2 className="hidden text-xl font-semibold text-gray-800 md:block">
          Kanban de Chamados
        </h2>
        <div className="flex items-center space-x-3 text-sm text-gray-600">
          <div
            className={`flex items-center space-x-2 ${
              desatualizado ? "text-amber-700" : ""
            }`}
            title={
              atualizadoEm
                ? `Dados buscados às ${atualizadoEm.toLocaleTimeString("pt-BR")}`
                : undefined
            }
          >
            <Clock className="w-4 h-4" />
            <span>
              {atualizadoEm
                ? `Atualizado em ${atualizadoEm.toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : "Carregando…"}
              {desatualizado && minutosDesdeAtualizacao !== null && (
                <span className="ml-1">· há {minutosDesdeAtualizacao} min</span>
              )}
            </span>
          </div>
          {onRefresh && (
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-lg transition-all duration-200 ${
                isRefreshing
                  ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                  : "text-blue-600 bg-blue-50 hover:bg-blue-100"
              }`}
              title={isRefreshing ? "Atualizando..." : "Atualizar dados"}
            >
              <RotateCcw
                className={`w-3 h-3 ${isRefreshing ? "animate-spin" : ""}`}
              />
              <span>{isRefreshing ? "Atualizando..." : "Atualizar"}</span>
            </button>
          )}
        </div>
      </div>

      {erroMover && (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          <span>{erroMover}</span>
          <button
            type="button"
            onClick={() => setErroMover(null)}
            className="shrink-0 text-red-500 hover:text-red-700"
            aria-label="Fechar aviso"
          >
            ×
          </button>
        </div>
      )}

      {/* No celular: as abas dizem quantos há em cada coluna e levam até ela. */}
      <div className="flex gap-2 md:hidden" role="tablist" aria-label="Colunas">
        {colunas.map(([status, config], i) => (
          <button
            key={status}
            type="button"
            role="tab"
            aria-selected={colunaAtiva === i}
            onClick={() => irParaColuna(i)}
            className={`flex-1 whitespace-nowrap rounded-full px-1.5 py-1.5 text-[11px] font-medium transition-colors ${
              colunaAtiva === i
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-600 border border-gray-200"
            }`}
          >
            {config.title} · {(chamadosPorStatus[status] || []).length}
          </button>
        ))}
      </div>

      {/* Kanban Board */}
      <DndContext
        sensors={sensores}
        // A coluna é a que está debaixo do ponteiro, não a que o cartão mais cobre.
        collisionDetection={pointerWithin}
        onDragStart={aoComecar}
        onDragEnd={aoSoltar}
        onDragCancel={() => setArrastando(null)}
      >
        <div
          ref={trilho}
          onScroll={aoRolar}
          className="-mx-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-4 md:mx-0 md:snap-none md:gap-6 md:px-0"
        >
          {colunas.map(([status, config]) => (
            <StatusCard
              key={status}
              title={config.title}
              chamados={chamadosPorStatus[status] || []}
              color={config.color}
              onChamadoClick={onChamadoClick}
              onPrioridadeUpdate={(id, prioridade) =>
                updatePrioridade({ id, prioridade })
              }
              isPrioridadeUpdating={isPrioridadeUpdating}
              isRefreshing={isRefreshing}
              selectedChamadoId={selectedChamadoId}
              isLoadingDetails={isLoadingDetails}
              showResponseTime={status === "Solicitados"}
              coluna={status}
              arrastavel={arrastavel}
            />
          ))}
        </div>
        {/* O próprio cartão acompanha o mouse, na largura da coluna (07/10/2026); o lugar de
            onde saiu fica esmaecido. No body: um ancestral com backdrop-filter (o painel do
            quadro) vira referência do position:fixed e deslocava o cartão para longe do mouse. */}
        {createPortal(
          <DragOverlay dropAnimation={null}>
            {arrastando && (
              <div
                style={{ width: larguraArrastado ?? undefined }}
                className="pointer-events-none rotate-1 cursor-grabbing rounded-xl shadow-2xl ring-2 ring-blue-300"
              >
                <ChamadoCard
                  chamado={arrastando}
                  showResponseTime={
                    normalizeStatus(arrastando.status.nome) === "Solicitados"
                  }
                />
              </div>
            )}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </div>
  );
};
